import { useState, useEffect } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Badge,
  Spinner,
  IconButton,
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
  MenuPositioner,
} from '@chakra-ui/react'
import { Drawer } from '@chakra-ui/react'
import { Download, Pencil, Trash2, X, MoreVertical, ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'
import type { DocumentWithRelations } from '../types'

interface DocumentPreviewDrawerProps {
  document: DocumentWithRelations | null
  isOpen: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  hasPrevious?: boolean
  hasNext?: boolean
  onEdit?: (document: DocumentWithRelations) => void
  onDownload?: (document: DocumentWithRelations) => void
  onDelete?: (document: DocumentWithRelations) => void
}

function isPDFDocument(mimeType: string): boolean {
  return mimeType === 'application/pdf'
}

function isImageDocument(mimeType: string): boolean {
  return mimeType.startsWith('image/')
}

function isVideoDocument(mimeType: string): boolean {
  return mimeType.startsWith('video/')
}

export function DocumentPreviewDrawer({
  document,
  isOpen,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious,
  hasNext,
  onEdit,
  onDownload,
  onDelete,
}: DocumentPreviewDrawerProps) {
  const { t } = useTranslation()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [menuJustClosed, setMenuJustClosed] = useState(false)

  // Load preview URL when document changes
  useEffect(() => {
    if (!document || !isOpen) {
      setPreviewUrl(null)
      return
    }

    const loadPreview = async () => {
      setIsLoadingPreview(true)
      try {
        // Get signed URL for preview (valid for 60 seconds)
        const { data, error } = await supabase.storage
          .from(document.storage_bucket)
          .createSignedUrl(document.storage_path, 60)

        if (error) throw error
        if (data?.signedUrl) {
          setPreviewUrl(data.signedUrl)
        }
      } catch (error) {
        console.error('Failed to load preview:', error)
        setPreviewUrl(null)
      } finally {
        setIsLoadingPreview(false)
      }
    }

    loadPreview()
  }, [document, isOpen])

  const handleInteractOutside = (e: { detail: { originalEvent: { target: EventTarget | null } } }) => {
    // Don't close drawer if menu is open or just closed
    if (isMenuOpen || menuJustClosed) {
      return
    }

    const target = e.detail.originalEvent.target as HTMLElement

    // Check if click is on a document card
    const documentCard = target.closest('[data-document-card]') as HTMLElement

    if (documentCard) {
      // Let the card's onClick handler manage the navigation
      return
    }

    // Check if click is on a menu item, menu trigger button, or any menu-related element
    const menuItem = target.closest('[role="menu"]') as HTMLElement
    const menuContent = target.closest('[role="menuitem"]') as HTMLElement
    const menuTrigger = target.closest('button[aria-label*="Actions"]') as HTMLElement
    const chakraMenuElement = target.closest('[data-scope="menu"]') as HTMLElement

    if (menuItem || menuContent || menuTrigger || chakraMenuElement) {
      // Let the menu handle the click
      return
    }

    // Click truly outside → Close drawer
    onClose()
  }

  if (!document) return null

  const canPreview = isPDFDocument(document.mime_type) ||
                     isImageDocument(document.mime_type) ||
                     isVideoDocument(document.mime_type)

  return (
    <Drawer.Root
      open={isOpen}
      placement="end"
      size="xl"
      modal={false}
      onInteractOutside={handleInteractOutside}
    >
      <Drawer.Positioner pointerEvents="none">
        <Drawer.Content pointerEvents="auto">
          <Drawer.Header borderBottomWidth="1px" py={4} overflow="visible">
            <Box display="flex" alignItems="center" w="full" gap={3}>
              {/* Navigation arrows - Left */}
              <Box display="flex" gap={1} flexShrink={0}>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigatePrevious}
                  disabled={!hasPrevious}
                  aria-label={t('common.previous')}
                  title={t('common.previous')}
                >
                  <ChevronUp size={18} />
                </IconButton>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateNext}
                  disabled={!hasNext}
                  aria-label={t('common.next')}
                  title={t('common.next')}
                >
                  <ChevronDown size={18} />
                </IconButton>
              </Box>

              {/* Title - Center */}
              <Box flex={1} minW={0}>
                <Heading
                  size="md"
                  lineHeight="1.3"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {document.display_name}
                </Heading>
                <HStack gap={1.5} mt={1}>
                  <Badge colorPalette="blue" size="sm">
                    {t(`documents.categories.${document.category}`)}
                  </Badge>
                  {document.document_type && (
                    <Badge colorPalette="gray" size="sm">
                      {document.document_type.replace(/_/g, ' ')}
                    </Badge>
                  )}
                </HStack>
              </Box>

              {/* Actions menu and close - Right */}
              <Box display="flex" gap={2} flexShrink={0}>
                <MenuRoot onOpenChange={(e) => {
                  setIsMenuOpen(e.open)
                  if (!e.open) {
                    // Menu just closed, set flag and clear after delay
                    setMenuJustClosed(true)
                    setTimeout(() => setMenuJustClosed(false), 300)
                  }
                }}>
                  <MenuTrigger asChild>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label={t('common.actions')}
                    >
                      <MoreVertical size={18} />
                    </IconButton>
                  </MenuTrigger>
                  <MenuPositioner>
                    <MenuContent minW="180px" zIndex={9999}>
                      {onEdit && (
                        <MenuItem value="edit" onClick={() => {
                          onEdit(document)
                        }}>
                          <HStack gap={2}>
                            <Pencil size={16} />
                            <Text>{t('common.edit')}</Text>
                          </HStack>
                        </MenuItem>
                      )}
                      {onDownload && (
                        <MenuItem value="download" onClick={() => {
                          onDownload(document)
                        }}>
                          <HStack gap={2}>
                            <Download size={16} />
                            <Text>{t('common.download')}</Text>
                          </HStack>
                        </MenuItem>
                      )}
                      {onDelete && (
                        <MenuItem
                          value="delete"
                          onClick={() => {
                            onDelete(document)
                          }}
                          color="red.600"
                          _hover={{ bg: 'red.50' }}
                        >
                          <HStack gap={2}>
                            <Trash2 size={16} />
                            <Text>{t('common.delete')}</Text>
                          </HStack>
                        </MenuItem>
                      )}
                    </MenuContent>
                  </MenuPositioner>
                  </MenuRoot>
                  <IconButton variant="ghost" size="sm" onClick={onClose} aria-label={t('common.cancel')}>
                    <X size={18} />
                  </IconButton>
                </Box>
            </Box>
          </Drawer.Header>

          <Drawer.Body>
            {/* Document Preview */}
            <Box
              h="full"
              borderWidth="1px"
              borderRadius="lg"
              overflow="hidden"
              bg="gray.50"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {isLoadingPreview ? (
                <VStack gap={3}>
                  <Spinner size="xl" color="brand.500" />
                  <Text color="gray.600">Chargement de l'aperçu...</Text>
                </VStack>
              ) : !canPreview ? (
                <VStack gap={3} p={8} textAlign="center">
                  <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                    Aperçu non disponible
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Ce type de fichier ne peut pas être prévisualisé.
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {document.original_filename}
                  </Text>
                </VStack>
              ) : !previewUrl ? (
                <VStack gap={3} p={8} textAlign="center">
                  <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                    Erreur de chargement
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Impossible de charger l'aperçu du document.
                  </Text>
                </VStack>
              ) : isPDFDocument(document.mime_type) ? (
                <iframe
                  src={previewUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                  title={document.display_name}
                />
              ) : isImageDocument(document.mime_type) ? (
                <img
                  src={previewUrl}
                  alt={document.display_name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : isVideoDocument(document.mime_type) ? (
                <video
                  src={previewUrl}
                  controls
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                >
                  Votre navigateur ne supporte pas la lecture de vidéos.
                </video>
              ) : null}
            </Box>
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  )
}
